import React from 'react';
import { IEntityContentItem } from './hooks/usePermissionEntityDescribeTable';
import styled from 'styled-components';
import { SvgIcon } from '../../components/Icon';
import { useNavigate } from 'react-router-dom';
import usePermission from '../../utils/hooks/permissionHooks/usePermission';
import { PermissionKey } from '../../utils/apollo/graphqlSchema';
import { theme } from '../../styles/theme';
import ThinDivider from '../../components/ThinDivider';
import KubitTooltip from '../../components/KubitTooltip';
import { PERMISSION_ERROR_MESSAGE } from '../../components/Chart/constants/utils';

interface IProps {
  contentList: IEntityContentItem[];
}

const Container = styled.div`
  display: flex;
  align-items: flex-start;
  a {
    color: ${theme.colors.secondary800};
    :hover {
      color: ${theme.colors.mainBlue};
    }
  }
`;

const Value = styled.div`
  color: ${theme.colors.secondary800};
`;

const Content = styled.div<{ $isFlexRow: boolean }>`
  display: flex;
  align-items: flex-start;
  column-gap: 32px;
  flex-direction: ${({ $isFlexRow }) => ($isFlexRow ? 'row' : 'column')};
  width: 100%;
`;

const ColumnList = styled.div<{ $isFlexRow: boolean }>`
  display: flex;
  flex-direction: column;
  row-gap: 8px;
  justify-content: center;
  width: ${({ $isFlexRow }) => ($isFlexRow ? '300px' : '600px')};
`;

const Title = styled.span`
  font-weight: 600;
  color: ${theme.colors.secondary900};
  margin-bottom: 4px;
`;

/**
 * PermissionEntityDescribeTable is a component to render a list of content for permission entity in overview pages
 * @param contentList is generated in usePermissionEntityDescribeTable, and passed to PermissionEntityDescribeTable
 *                    to render the content list.
 */
const PermissionEntityDescribeTable = ({ contentList }: IProps) => {
  const { checkIsPermitted } = usePermission();
  const navigate = useNavigate();
  const isPermittedToEdit = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.AdminEdit],
  });

  return (
    <>
      {contentList.map((content, index) => {
        const actionProps = content?.actionProps;

        return (
          <React.Fragment key={'content-list-item-' + index}>
            <Container key={'permission-content-' + index}>
              <Content $isFlexRow={content.isFlexRow}>
                {content.list.map((contentItem, index) => {
                  return (
                    <ColumnList
                      key={contentItem.title + index}
                      $isFlexRow={content.isFlexRow}
                    >
                      <Title>{contentItem.title}</Title>
                      {contentItem.items.map((child, childIndex: number) => (
                        <Value key={'content-item-' + childIndex}>
                          {child}
                        </Value>
                      ))}
                    </ColumnList>
                  );
                })}
              </Content>
              {/* if there is a svg button for edit entity */}
              {actionProps && (
                <KubitTooltip
                  content={
                    isPermittedToEdit
                      ? actionProps.hint
                      : PERMISSION_ERROR_MESSAGE
                  }
                  placement="top-end"
                  noOpenDelay={true}
                >
                  <SvgIcon
                    name={actionProps.name}
                    width={16}
                    height={16}
                    isDisabled={!isPermittedToEdit}
                    isHoverable={true}
                    onClick={
                      isPermittedToEdit
                        ? () => navigate(actionProps.link)
                        : undefined
                    }
                  />
                </KubitTooltip>
              )}
            </Container>
            {index !== contentList.length - 1 && (
              <ThinDivider layout="horizontal" size="full" />
            )}
          </React.Fragment>
        );
      })}
    </>
  );
};

export default PermissionEntityDescribeTable;
